import { debounce } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Field } from "react-final-form";

import { IHandBookSegment } from "pages/HandBook/model/types";
import { findSegmentsById } from "pages/HandBook/utils/segments";

import { useTypedSelector } from "app/store/typedUseSelector";
import InputAutocomplete from "shared/ui/inputs/InputAutocomplete/InputAutocomplete";
import { INPUT_BASE_VARIANTS } from "shared/ui/inputs/InputBase";

import useDebouncedValue from "utils/hooks/useDebouncedValue";

import styles from "./TemplateExpenditureSegments.module.scss";

const AUTOCOMPLETE_DEBOUNCE_DELAY_MS = 400;

interface TemplateExpenditureSegmentsProps {
  defaultSegment?: number | null;
  onChangeSegment: (segment: number | string | null) => void;
  onChangeParentSegment: (segment: number | string | null) => void;
}

const TemplateExpenditureSegments: React.FC<TemplateExpenditureSegmentsProps> = ({
  defaultSegment,
  onChangeSegment,
  onChangeParentSegment,
}) => {
  const allSegments = useTypedSelector((state) => state.handBookReducer.allSegments);
  const [searchParentSegment, setSearchParentSegment] = useState("");
  const debouncedSearchParentSegment = useDebouncedValue(searchParentSegment, AUTOCOMPLETE_DEBOUNCE_DELAY_MS);
  const [searchSegment, setSearchSegment] = useState("");
  const debouncedSearchSegment = useDebouncedValue(searchSegment, AUTOCOMPLETE_DEBOUNCE_DELAY_MS);
  /** Выбранный родительский сегмент */
  const [selectedSegment, setSelectedSegment] = useState<IHandBookSegment | undefined>();

  useEffect(() => {
    if (!allSegments.length || !defaultSegment) return;
    // defaultSegment Всегда id дочернего сегмента
    const { segment, parentSegment } = findSegmentsById(allSegments, defaultSegment);
    if (parentSegment) {
      setSelectedSegment(parentSegment);
      setSearchParentSegment(parentSegment.name);
      //   onChangeParentSegment(parentSegment.id);
    }
    if (segment) {
      setSearchSegment(segment.name);
      //   onChangeSegment(segment.id);
    }
  }, []);

  const parentSegmentsOptions = useMemo(
    () =>
      allSegments.filter(
        (s) =>
          s.children?.length > 0 && s.name.toLowerCase().indexOf(debouncedSearchParentSegment.trim().toLowerCase()) > -1
      ),
    [debouncedSearchParentSegment, allSegments]
  );

  const segmentsOptions = useMemo(
    () =>
      selectedSegment?.children?.filter(
        (s) => s.name.toLowerCase().indexOf(debouncedSearchSegment.trim().toLowerCase()) > -1
      ) || [],
    [debouncedSearchSegment, selectedSegment]
  );

  const isShowChildSegmentRequired = !!searchParentSegment && !searchSegment;

  const debouncedChangeSelectedSegmentId = (name: string) => {
    const parentCandidate = allSegments?.find((s: IHandBookSegment) => s.name === name.trim());
    setSelectedSegment(parentCandidate);
    if (parentCandidate) {
      onChangeParentSegment(parentCandidate.id);
    }
  };

  return (
    <div className={styles.container}>
      <Field
        name="parent_segment"
        render={({ input, meta }) => (
          <InputAutocomplete
            openOnFocus
            onChangeSearchBar={(v: string) => {
              // Сегменты – либо id, либо названия. Названия проверяются на совпадение с существующими, если не совпали – создаём новые сегменты
              input.onChange(v);
              setSearchParentSegment(v);
              debouncedChangeSelectedSegmentId(v);
              // Изменение Атрибута 1 приводит к очистке Атрибута 2
              setSearchSegment("");
              onChangeSegment(null);
            }}
            isLoading={searchParentSegment !== debouncedSearchParentSegment}
            onSelectOption={(name, id) => {
              input.onChange(id);
              setSearchParentSegment(name);
              onChangeParentSegment(+id);
              setSelectedSegment(allSegments?.find((s) => s.id === id));
              // Изменение Атрибута 1 приводит к очистке Атрибута 2
              setSearchSegment("");
              onChangeSegment(null);
            }}
            options={parentSegmentsOptions}
            displayingValue={searchParentSegment}
            placeholder="Атрибут 1"
            variant={INPUT_BASE_VARIANTS.SECONDARY}
          />
        )}
      />
      <Field
        name="segment"
        render={({ input, meta }) => (
          <InputAutocomplete
            openOnFocus
            onChangeSearchBar={(v: string) => {
              input.onChange(v);
              setSearchSegment(v);
              onChangeSegment(v);
            }}
            isLoading={searchSegment !== debouncedSearchSegment}
            onSelectOption={(name, id) => {
              input.onChange(name);
              setSearchSegment(name);
              onChangeSegment(name);
            }}
            options={segmentsOptions}
            displayingValue={searchSegment}
            placeholder="Атрибут 2"
            disabled={!searchParentSegment}
            error={isShowChildSegmentRequired ? "Укажите атрибут 2" : meta.error}
            variant={INPUT_BASE_VARIANTS.SECONDARY}
          />
        )}
      />
    </div>
  );
};

export default TemplateExpenditureSegments;
